<script lang="ts" setup>
import { NuxtLink } from '#components'
import type { RouteLocationRaw } from '#vue-router'

const props = defineProps<{
  disabled?: boolean | None
  to?: RouteLocationRaw | 'span' | None
  right?: boolean
  txt: null | undefined | stringI18n
  label?: 'never'
  size?: '18' | '28' | '36' | '44' | '40' | '48' | '50'
  active?: boolean
  loading?: boolean
}>()

function labelWidth(size: number | None) {
  if (!size) return '60px'
  // 8px padding + 7px for each letter
  return 28 + size * 6 + 'px'
}

const style = computed(() => ({ '--lw': labelWidth(props.txt?.length) }))
</script>

<template>
  <component
    :is="to === 'span' ? 'span' : to ? NuxtLink : 'button'"
    :type="to ? undefined : 'button'"
    :to
    :disabled
    class="_relative _svg"
    :class="[$style.ui, disabled ? '_c4' : $style.active, active && 'on', $style[`s${size || 40}`]]"
  >
    <UiLoading v-if="loading" :class="$style.loading" class="_absolute _inset" />

    <slot><IconExit :stroke-width="3" :size="10" /></slot>

    <span v-if="txt" :class="[$style.label, right && $style.right]" :style>
      <span class="_fw-xl" :class="$style.txt">{{ txt }}</span>
    </span>
  </component>
</template>

<style module>
.loading {
  composes: global(_br99);
  composes: global(_cb3);
  background: #fafafac8;
}
.ui {
  composes: global(_br99);
  z-index: 2;
  --lw: 60px;
}

.s18 {
  --svg: 18px;
}
.s28 {
  --svg: 28px;
}
.s36 {
  --svg: 36px;
}
.s40 {
  --svg: 40px;
}
.s44 {
  --svg: 44px;
}
.s48 {
  --svg: 48px;
}
.s50 {
  --svg: 50px;
}

:global(.d) .active:hover,
.active:focus-visible,
.active:global(.on) {
  color: var(--black);
}

.label {
  composes: global(_br99);
  composes: global(_absolute);

  composes: global(_fs09);
  composes: global(_ls-xs);
  composes: global(_pl14);
  composes: global(_pr14);
  composes: global(_b5);
  composes: global(_hidden);
  composes: global(_flexline);

  composes: global(_bg0);
  composes: global(_ws_nowrap);

  align-items: center;

  top: -1px;
  bottom: -1px;
  right: -1px;
  z-index: -1;

  transform: scale(0);
  width: calc(var(--svg) + 2px);
  box-shadow:
    1px 2px 0 hsla(240, 4%, 66%, 0.1),
    2px 4px 11px 2px hsla(240, 4%, 80%, 0.1);
  transition:
    width 100ms var(--tran2) 150ms,
    transform 200ms var(--tran2) 300ms,
    box-shadow 200ms var(--tran2);
}
.right {
  left: -1px;
  right: unset;
  composes: global(_end);
}

:global(.ButtonIcon) .label,
.active:global(.on) .label {
  background: var(--bg);
}
:global(.d) .active:hover .label,
.active:active .label,
.active:focus-visible .label,
.active:global(.on) .label,
:global(.d) :global(.ButtonIcon):hover .active .label {
  width: calc(var(--svg) + var(--lw));
  transform: scale(1);
  transition:
    width 100ms var(--tran2) 200ms,
    transform 200ms var(--tran2) 0ms,
    box-shadow 100ms var(--tran2);
}
.active:focus-visible .label,
.active:active .label {
  --b: var(--blue7);
  background: var(--bg);
  border-color: var(--b);
  box-shadow: 0 0 0 1px var(--b);
}

.txt {
  composes: global(_c1);
  transition: opacity 100ms var(--tran2) 100ms;
  opacity: 0.01;
}
:global(.d) .active:hover .txt,
.active:active .txt,
.active:global(.on) .txt,
.active:focus-visible .txt,
:global(.ButtonIcon):hover .txt {
  opacity: 0.99;
  transition-delay: 300ms;
}
:global(.ButtonIcon):hover .active,
:global(.d) .active:hover {
  color: var(--black);
}
</style>
